import { graphql } from 'gatsby'
import React, { useCallback, useContext, useMemo, useState } from 'react'

import { MainMenuFragmentFragment } from '../../../graphql/gatsby'
import useSiteSettings from '../../../hooks/useSiteSettings'
import { publishViewCartEvent } from '../../../lib/analytics/cart'
import { ToggleCartContext, useCartContext } from '../../../lib/cart'
import { totalQuantityExcludingDependents } from '../../../lib/cart/item'
import { useShouldHideHeader } from '../../../lib/header/hooks/useShouldHideHeader'
import {
  setIsMobileMenuOpen,
  setShowSearch,
  useIsMobileMenuOpen,
  useShowSearch,
  useUtilityMenuState,
} from '../../../lib/header/state'
import {
  stringifyHeaderStyle,
  useHeaderStyle,
  whiteHeaderStyleClasses,
} from '../../../lib/header/style'
import { useSanityRoleAndContextBasedContent } from '../../../lib/sanity'
import {
  isAccountMenuEntry,
  isECatalogMenuEntry,
  isEScriptMenuEntry,
  isInteractionCheckerMenuEntry,
  isLoginMenuEntry,
  isRegistrationMenuEntry,
} from '../../../utils/menuEntryTools'
import { filterMenuEntriesToDisplay } from '../../../utils/sanity/documents/menuEntry'
import { cn } from '../../utils/cn'
import Link from '../Link'
import CartIcon from './CartIcon'
import { MenuEntryContent } from './menu-entry-content/MenuEntryContent'
import MenuEntryLink from './MenuEntryLink'
import { SearchContainer } from './SearchContainer'
import SubMenuEntry from './SubMenuEntry'
import * as UI from './ui'

import CaretLeftIcon from '../../../img/icons/caret-left.svg'
import SearchIcon from '../../../img/icons/search.svg'

interface MainMenuProps {
  isAuthenticated: boolean
  isMenuOpen: boolean
  onSelectSubMenu: (submenu?: string | null) => void
  entries: NonNullable<NonNullable<MainMenuFragmentFragment['mainMenu']>[number]>[]
}

interface SubMenuProps {
  onBack: () => void
  onSelectSubMenu?: (href?: string) => void
  subMenu: string
  entries: NonNullable<NonNullable<MainMenuFragmentFragment['mainMenu']>[number]>[]
}

export const MobileHeader: React.FC = () => {
  const [activeSubMenu, setActiveSubMenu] = useState<string | undefined | null>(null)
  const { cart } = useCartContext()
  const totalQuantity = totalQuantityExcludingDependents(cart?.items, cart?.total_quantity ?? 0)
  const { toggleCart: toggleCartModal } = useContext(ToggleCartContext)
  const { mobileMenu: menuEntriesUnfiltered } = useSiteSettings() || {}
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const entries = useMemo(
    () => filterMenuEntriesToDisplay(shouldDisplayContent)(menuEntriesUnfiltered),
    [menuEntriesUnfiltered, shouldDisplayContent],
  )
  const showSearch = useShowSearch()
  const isMenuOpen = useIsMobileMenuOpen()
  const shouldHideHeader = useShouldHideHeader()
  const { isAuthenticated } = useUtilityMenuState()
  const { headerStyle, classes: headerStyleClasses } = useHeaderStyle()
  const handleStyleChangeOnMenuOpen =
    headerStyle === 'transparent' && isMenuOpen
      ? stringifyHeaderStyle(whiteHeaderStyleClasses)
      : headerStyleClasses.backgroundColor

  const toggleSearch = () => {
    setShowSearch(!showSearch)
  }

  const handleCartButtonClick = () => {
    toggleCartModal()
    if (cart) {
      publishViewCartEvent(cart)
    }
  }

  const toggleMenu = () => {
    setIsMobileMenuOpen(!isMenuOpen)
    if (isMenuOpen) {
      setActiveSubMenu(null)
    }
  }
  const closeMenu = useCallback(() => {
    setIsMobileMenuOpen(false)
    setActiveSubMenu(null)
  }, [setActiveSubMenu])

  return (
    <>
      <UI.Header
        className={cn(
          'px-5 py-2.5',
          shouldHideHeader ? '-translate-y-[200px]' : 'translate-y-0',
          handleStyleChangeOnMenuOpen,
        )}
      >
        <div
          className={cn('flex h-auto items-center justify-between', headerStyleClasses.textColor)}
        >
          <Link to="/" className="flex flex-row gap-6">
            <UI.DFHLogo />
            <hr className="h-[30px] my-auto border-r border-brand-rule-grey ml-2" />
            <UI.N1PractitionerSupplementBrandLogo />
          </Link>

          <div className="flex items-center space-x-4">
            <UI.Button
              onMouseDown={(e) => e.stopPropagation()}
              onClick={toggleSearch}
              className="p-2"
              aria-label="Toggle search"
            >
              <UI.Icon src={SearchIcon} alt="Search" aria-hidden="true" />
            </UI.Button>
            <UI.Button
              onClick={handleCartButtonClick}
              className="p-2"
              aria-label="Toggle mini cart"
            >
              <CartIcon className="font-gotham w-5 h-5" quantity={totalQuantity} />
            </UI.Button>
            <UI.Button onClick={toggleMenu} className="p-2" aria-label="Toggle menu">
              <UI.MenuIcon isMenuOpen={isMenuOpen} />
            </UI.Button>
          </div>
        </div>
      </UI.Header>

      <SearchContainer />

      <div
        className={cn(
          'transform transition-transform duration-300 ease-in-out',
          isMenuOpen ? 'translate-x-0' : '-translate-x-full',
          // calculate the height of the brand menu + header (83px)
          'relative flex flex-col h-[calc(100dvh-83px)] pt-10 pb-[30px] px-5',
          stringifyHeaderStyle(whiteHeaderStyleClasses),
          'overflow-hidden',
        )}
      >
        <div
          className={cn(
            'absolute inset-0 flex transition-transform duration-300',
            activeSubMenu ? '-translate-x-full' : 'translate-x-0',
          )}
        >
          <MainMenu
            isAuthenticated={isAuthenticated}
            isMenuOpen={isMenuOpen}
            onSelectSubMenu={setActiveSubMenu}
            entries={entries}
          />
        </div>

        <div
          className={cn(
            'absolute inset-0 flex transition-transform duration-300',
            activeSubMenu !== null ? 'translate-x-0' : 'translate-x-full',
          )}
        >
          {activeSubMenu && (
            <SubMenu
              onBack={() => setActiveSubMenu(null)}
              subMenu={activeSubMenu}
              entries={entries}
              onSelectSubMenu={closeMenu}
            />
          )}
        </div>
      </div>
    </>
  )
}

const MainMenu: React.FC<MainMenuProps> = ({
  isAuthenticated,
  isMenuOpen,
  onSelectSubMenu,
  entries,
}) => {
  const { headerStyle } = useHeaderStyle()
  const menuEntryElements = useMemo(
    () =>
      entries.reduce((acc: React.ReactElement[], entry) => {
        if (!entry) return acc
        if (
          isRegistrationMenuEntry(entry) ||
          isLoginMenuEntry(entry) ||
          isECatalogMenuEntry(entry) ||
          isAccountMenuEntry(entry) ||
          isEScriptMenuEntry(entry) ||
          isInteractionCheckerMenuEntry(entry)
        )
          return acc

        const hasChildEntries = entry.childEntries && entry.childEntries.length > 0
        acc.push(
          <li key={entry._id || entry.name}>
            {hasChildEntries ? (
              <UI.Button onClick={() => onSelectSubMenu(entry.name)}>{entry.name}</UI.Button>
            ) : (
              <MenuEntryLink
                className="hover:!font-medium"
                link={entry.link}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {entry.name}
              </MenuEntryLink>
            )}
          </li>,
        )

        return acc
      }, []),
    [entries, onSelectSubMenu],
  )

  const utilityMenuEntries = useMemo(
    () =>
      entries.reduce((acc: React.ReactElement[], entry) => {
        if (!entry) return acc
        if (
          isECatalogMenuEntry(entry) ||
          isAccountMenuEntry(entry) ||
          isEScriptMenuEntry(entry) ||
          isInteractionCheckerMenuEntry(entry)
        ) {
          acc.push(
            <li>
              <MenuEntryLink
                dispensaryContactInformationRequired={entry.dispensaryContactInformationRequired}
                className="hover:!font-medium"
                link={entry.link}
              >
                {entry.name}
              </MenuEntryLink>
            </li>,
          )
        }
        return acc
      }, []),
    [entries],
  )

  const loginAndRegistrationElements = useMemo(
    () =>
      entries.reduce((acc: React.ReactElement[], entry) => {
        if (!entry) return acc
        if (isRegistrationMenuEntry(entry) || isLoginMenuEntry(entry)) {
          acc.push(
            <li>
              <MenuEntryLink className="!font-gotham hover:!font-medium" link={entry.link}>
                {entry.name}
              </MenuEntryLink>
            </li>,
          )
        }
        return acc
      }, []),
    [entries],
  )

  return (
    <nav
      className={cn(
        'flex flex-col w-full py-10 px-5 !font-gotham !text-2xl !font-normal',
        isMenuOpen && headerStyle === 'transparent' && 'border-t border-t-brand-rule-grey',
      )}
    >
      <UI.List className="border-b border-brand-navy-3 pb-10">{menuEntryElements}</UI.List>

      {isAuthenticated ? (
        <>
          <UI.List className="py-10">{utilityMenuEntries}</UI.List>

          <div className="flex border-t-4 mt-auto pt-10 border-brand-navy-3">
            <UI.LogoutLink />
          </div>
        </>
      ) : (
        <UI.List className="py-10">{loginAndRegistrationElements}</UI.List>
      )}
    </nav>
  )
}

const SubMenu: React.FC<SubMenuProps> = ({ onBack, subMenu, entries, onSelectSubMenu }) => {
  const { shouldDisplayContent } = useSanityRoleAndContextBasedContent()
  const filteredEntry = useMemo(() => {
    return entries.filter((entry) => entry?.name === subMenu)[0]
  }, [entries, subMenu])

  const subMenuEntries = useMemo(
    () => filterMenuEntriesToDisplay(shouldDisplayContent)(filteredEntry?.childEntries),
    [filteredEntry?.childEntries, shouldDisplayContent],
  )

  const subMenuEntryElements = useMemo(
    () =>
      subMenuEntries.reduce((acc: React.ReactElement[], entry) => {
        if (!entry) return acc
        acc.push(
          <li className="hover:!font-medium">
            <SubMenuEntry onClick={onSelectSubMenu} key={entry._id} menuEntry={entry} />
          </li>,
        )
        return acc
      }, []),
    [subMenuEntries, onSelectSubMenu],
  )

  const parentEntry = entries.find((entry) => entry?.name === subMenu)
  const contentToShow = parentEntry?.content

  return (
    <nav className="flex flex-col w-full py-10 px-5 !font-gotham !text-2xl !font-normal overflow-y-auto">
      <div className="mb-6">
        <UI.Button onClick={onBack} className="flex items-center font-medium">
          <UI.Icon className="mr-2 brightness-100 invert-0" src={CaretLeftIcon} />
          {subMenu}
        </UI.Button>
      </div>

      <UI.List className="h-full">{subMenuEntryElements}</UI.List>

      {contentToShow && (
        <div>
          <hr className="border-b border-brand-navy-3 my-10" />
          <MenuEntryContent content={contentToShow} />
        </div>
      )}
    </nav>
  )
}

export const mobileMenu = graphql`
  fragment mobileMenuFragment on SanitySiteSettings {
    mobileMenu {
      ...MenuEntryFragment
      childEntries {
        ...MenuEntryFragment
        childEntries {
          ...MenuEntryFragment
          childEntries {
            ...MenuEntryFragment
          }
        }
      }
    }
  }
`
